import { z } from "zod";

import {
  DeliveryQuoteDeliveryType,
  DeliveryQuoteFragmentFragment,
  DeliveryQuoteStatus,
  ProductBySlugQuery_productBySlug_Product,
} from "@/Apollo/schema";
import {
  getGraphQLErrorCode,
  isSessionRejected,
} from "@/common/utils/graphql-error.utils";
import {
  US_STATE_CODES,
  UsStateCode,
} from "@/features/us-delivery/static/us-states.static";
import { isUsLocale } from "@/i18n/config";

/**
 * WHO-6509. Which delivery block a product page shows on `/en-us`:
 *
 * - `quote`: a courier listing (`deliveryByQuote`, WHO-6499): "Request a quote".
 * - `listed`: every other US-shippable listing: the delivery time and duties, no price until the
 *   gateway serves the Sendcloud US rate (today's options are priced for a European location).
 * - `none`: not `/en-us`, or a listing that doesn't ship to the US (its notice says so, WHO-6510),
 *   so the page shows what it shows everywhere else.
 *
 * The Sendcloud US rate (WHO-6587) and "we don't deliver there" (WHO-6640) come in as kinds of
 * their own once their gateway fields are live.
 */
export type UsDeliveryKind = "none" | "quote" | "listed";

export const getUsDeliveryKind = (
  product: Pick<
    ProductBySlugQuery_productBySlug_Product,
    "usShippable" | "deliveryByQuote"
  >,
  locale: string | undefined
): UsDeliveryKind => {
  /* Both fields are only selected on /en-us (`@include(if: $usMarket)`): absent means unknown. */
  if (!isUsLocale(locale) || product.usShippable !== true) {
    return "none";
  }

  return product.deliveryByQuote ? "quote" : "listed";
};

/** What the quote block shows for the buyer's newest quote (states A–D in WHO-6499). */
export type UsQuoteView =
  | "none"
  | "requested"
  | "quoted"
  | "expired"
  | "sold"
  | "paid";

const QUOTE_VIEW_BY_STATUS: Record<DeliveryQuoteStatus, UsQuoteView> = {
  [DeliveryQuoteStatus.Requested]: "requested",
  [DeliveryQuoteStatus.Quoted]: "quoted",
  [DeliveryQuoteStatus.Expired]: "expired",
  [DeliveryQuoteStatus.ItemSold]: "sold",
  [DeliveryQuoteStatus.Paid]: "paid",
};

/**
 * A quoted fee past its date reads as expired: the gateway does the same when it answers, and
 * this covers a page left open across the deadline.
 */
export const getUsQuoteView = (
  quote: Pick<DeliveryQuoteFragmentFragment, "status" | "validUntil"> | null,
  now: Date
): UsQuoteView => {
  if (!quote) {
    return "none";
  }

  if (
    quote.status === DeliveryQuoteStatus.Quoted &&
    quote.validUntil &&
    new Date(quote.validUntil).getTime() <= now.getTime()
  ) {
    return "expired";
  }

  return QUOTE_VIEW_BY_STATUS[quote.status];
};

/** A closed quote can be asked again; the gateway opens a new one (WHO-6499). */
export const canRequestAgain = (view: UsQuoteView): boolean =>
  view === "expired" || view === "sold" || view === "paid";

/** The dashboard's limit on the buyer's note (`MAX_NOTE_LENGTH`). */
export const US_QUOTE_NOTE_MAX_LENGTH = 1000;

/** The dashboard's limit on the city (`len(city) > 128`). */
export const US_QUOTE_CITY_MAX_LENGTH = 128;

/** Five digits or ZIP+4, as the dashboard checks it (`US_ZIP`). */
const US_ZIP = /^\d{5}(-\d{4})?$/;

/** Messages are field keys of `US_QUOTE_FORM_ERROR_COPY`, so the form renders them with a default. */
export const usQuoteFormSchema = z.object({
  postalCode: z.string().trim().regex(US_ZIP, { message: "postalCode" }),
  region: z.enum(US_STATE_CODES, {
    errorMap: () => ({ message: "region" }),
  }),
  city: z
    .string()
    .trim()
    .min(1, { message: "city" })
    .max(US_QUOTE_CITY_MAX_LENGTH, { message: "city" }),
  deliveryType: z.nativeEnum(DeliveryQuoteDeliveryType),
  note: z
    .string()
    .trim()
    .max(US_QUOTE_NOTE_MAX_LENGTH, { message: "note" })
    .optional(),
});

export type UsQuoteFormValues = z.infer<typeof usQuoteFormSchema>;

const isUsStateCode = (value: string): value is UsStateCode =>
  (US_STATE_CODES as readonly string[]).includes(value);

/** The previous quote's address, to ask again without typing it twice; the note is not kept. */
export const toFormValues = (
  quote: Pick<
    DeliveryQuoteFragmentFragment,
    "postalCode" | "region" | "city" | "deliveryType"
  >
): Partial<UsQuoteFormValues> => ({
  postalCode: quote.postalCode,
  region: isUsStateCode(quote.region) ? quote.region : undefined,
  city: quote.city,
  deliveryType: quote.deliveryType,
});

/**
 * The copy for each refused request; anything not named here is `generic`. The gateway's own
 * rate limit (10 an hour) answers in words, not a code: `getRateLimitCopy` reads those.
 *
 * - `sessionExpired`: the session ended while the page was open (`UNAUTHENTICATED` or a 401), so
 *   the buyer logs in again rather than retrying.
 * - `unavailable`: the gateway's switch is off (`FEATURE_DISABLED`), for instance on a page cached
 *   before it went off. Trying again won't help, so the copy doesn't invite it.
 */
export type UsQuoteRequestError =
  | "invalidAddress"
  | "limitReached"
  | "notQuotable"
  | "sessionExpired"
  | "unavailable"
  | "generic";

const REQUEST_ERROR_BY_CODE: Record<string, UsQuoteRequestError> = {
  INVALID_ADDRESS: "invalidAddress",
  LIMIT_REACHED: "limitReached",
  LISTING_NOT_QUOTABLE: "notQuotable",
  FEATURE_DISABLED: "unavailable",
};

export const getUsQuoteRequestError = (error: unknown): UsQuoteRequestError => {
  if (isSessionRejected(error)) {
    return "sessionExpired";
  }

  const code = getGraphQLErrorCode(error);

  return (code && REQUEST_ERROR_BY_CODE[code]) || "generic";
};
